import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Copy, Plus, ArrowClockwise, Trash, Eye, Package } from '@phosphor-icons/react';
import ListSearch from '../../components/ListSearch';
import SearchableSelect from '../../components/SearchableSelect';
import { apiPath, BACKEND_URL } from '../../config/apiBase';
import { notifyError, notifySuccess } from '../../lib/notify';
import { LauncherConfigFields, LauncherPreview } from './launcherIcons';

const CW = apiPath('/api/client-widgets');

function authHeaders() {
  const token = localStorage.getItem('token');
  return { withCredentials: true, headers: { Authorization: `Bearer ${token}` } };
}

function errText(err, fallback) {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #D1D5DB',
  borderRadius: 8,
  fontSize: 13,
};

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 };

const DEFAULT_FORM_FIELDS = {
  reporter_name: { enabled: true, required: true, label: 'Your name' },
  reporter_email: { enabled: true, required: true, label: 'Your email' },
  reporter_phone: { enabled: true, required: false, label: 'Phone' },
  subject: { enabled: true, required: true, label: 'Subject' },
  description: { enabled: true, required: false, label: 'Description' },
  priority: { enabled: true, required: false, label: 'Priority' },
  attachments: { enabled: true, required: false, label: 'Attachments' },
};

const FORM_FIELD_ROWS = [
  { key: 'reporter_name', title: 'Name', lock: false },
  { key: 'reporter_email', title: 'Email', lock: true },
  { key: 'reporter_phone', title: 'Phone', lock: false },
  { key: 'subject', title: 'Subject', lock: false },
  { key: 'description', title: 'Description', lock: false },
  { key: 'priority', title: 'Priority', lock: false },
  { key: 'attachments', title: 'Attachments', lock: false },
];

function hostFromAppUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    const parsed = new URL(raw.includes('://') ? raw : `https://${raw}`);
    return (parsed.hostname || '').toLowerCase();
  } catch {
    return '';
  }
}

function mergeDomainLine(existingText, host) {
  const lines = String(existingText || '')
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (host && !lines.some((d) => d.toLowerCase() === host)) {
    lines.push(host);
  }
  return lines.join('\n');
}

function normalizeFormFields(raw) {
  const src = raw && typeof raw === 'object' ? raw : {};
  const out = {};
  Object.keys(DEFAULT_FORM_FIELDS).forEach((key) => {
    const base = DEFAULT_FORM_FIELDS[key];
    const row = src[key] && typeof src[key] === 'object' ? src[key] : {};
    let enabled = row.enabled != null ? !!row.enabled : !!base.enabled;
    let required = row.required != null ? !!row.required : !!base.required;
    if (key === 'reporter_email') {
      enabled = true;
      required = true;
    }
    if (!enabled) required = false;
    out[key] = {
      enabled,
      required,
      label: (row.label || base.label || key).trim() || base.label,
    };
  });
  return out;
}

/**
 * Product Management → Client widgets
 * Lists every created widget; filter by product optional. Edit color/toggles → Save.
 */
/**
 * ``lockedProductId``: rendered inside one product (Product → Client widgets) —
 * only that product's widgets, no product strip.
 */
export default function ClientWidgetsPanel({ products, lockedProductId = '' }) {
  const [selectedProductId, setSelectedProductId] = useState(products[0]?.product_id || '');
  const [createProductId, setCreateProductId] = useState(products[0]?.product_id || '');
  const [sites, setSites] = useState([]);
  const [clients, setClients] = useState([]);
  const [defaultsDraft, setDefaultsDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newClient, setNewClient] = useState('');
  const [newBranch, setNewBranch] = useState('');
  const [newSelectionMode, setNewSelectionMode] = useState('fixed');
  const [newAppUrl, setNewAppUrl] = useState('');
  const [branches, setBranches] = useState([]);
  const [hasBranchCatalog, setHasBranchCatalog] = useState(false);
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [keyVisible, setKeyVisible] = useState(false);
  // Signing secret is shown only right after it is generated (never stored in the UI).
  const [identitySecret, setIdentitySecret] = useState(null);
  const [productSearch, setProductSearch] = useState('');

  const selectionModeOptions = [
    { value: 'fixed', label: 'Fixed company (+ optional branch)' },
    { value: 'pick_branch', label: 'Fixed company, user selects branch in widget' },
    { value: 'pick_company', label: 'User selects company in widget' },
    { value: 'pick_company_branch', label: 'User selects company + branch in widget' },
  ];

  const productLabel = (s) => s?.product_name || s?.product_id || 'Unknown product';

  const needsCompanyAtCreate = (mode) => mode === 'fixed' || mode === 'pick_branch';

  const placementLabel = (s) => {
    const mode = s?.client_selection_mode || 'fixed';
    if (mode === 'pick_company') return 'User picks company';
    if (mode === 'pick_company_branch') return 'User picks company + branch';
    if (mode === 'pick_branch') {
      return s?.client_name ? `${s.client_name} · user picks branch` : 'User picks branch';
    }
    if (s?.branch) return `${s.client_name} / ${s.branch}`;
    return s?.client_name || 'Fixed company';
  };

  const siteTitle = (s) => `${productLabel(s)} · ${placementLabel(s)}`;

  const productOptions = useMemo(
    () => (products || []).map((p) => ({ value: p.product_id, label: p.name || p.product_id })),
    [products],
  );

  const clientOptions = useMemo(
    () => (clients || []).map((c) => ({
      value: c.value,
      label: c.parent_value ? `[${c.parent_value}] ${c.label || c.value}` : (c.label || c.value),
      searchText: `${c.parent_value || ''} ${c.label || ''} ${c.value || ''}`,
    })),
    [clients],
  );

  const branchOptions = useMemo(() => {
    const opts = (branches || []).map((b) => ({
      value: b.value,
      label: b.label || b.value,
    }));
    return [{ value: '', label: 'Company-level (no branch)' }, ...opts];
  }, [branches]);

  const selected = useMemo(
    () => sites.find((s) => s.site_id === selectedSiteId) || null,
    [sites, selectedSiteId],
  );

  const groupedSites = useMemo(() => {
    const order = [];
    const map = new Map();
    sites.forEach((s) => {
      const key = s.product_id || '_unknown';
      if (!map.has(key)) {
        map.set(key, {
          productId: key,
          productName: s.product_name || key,
          items: [],
        });
        order.push(key);
      }
      map.get(key).items.push(s);
    });
    return order.map((k) => map.get(k));
  }, [sites]);

  const productStrip = useMemo(() => {
    const countByProduct = new Map();
    sites.forEach((s) => {
      const key = s.product_id || '_unknown';
      countByProduct.set(key, (countByProduct.get(key) || 0) + 1);
    });
    const fromCatalog = (products || []).map((p) => ({
      productId: p.product_id,
      productName: p.name || p.product_id,
      productKey: p.key || '',
      widgetCount: countByProduct.get(p.product_id) || 0,
    }));
    const known = new Set(fromCatalog.map((p) => p.productId));
    groupedSites.forEach((g) => {
      if (!known.has(g.productId)) {
        fromCatalog.push({
          productId: g.productId,
          productName: g.productName,
          productKey: '',
          widgetCount: g.items.length,
        });
      }
    });
    return fromCatalog;
  }, [products, sites, groupedSites]);

  const filteredProductStrip = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return productStrip;
    return productStrip.filter((p) => (
      `${p.productName || ''} ${p.productKey || ''} ${p.productId || ''}`.toLowerCase().includes(q)
    ));
  }, [productStrip, productSearch]);

  const productSites = useMemo(
    () => sites.filter((s) => s.product_id === selectedProductId),
    [sites, selectedProductId],
  );

  const selectedProduct = useMemo(
    () => productStrip.find((p) => p.productId === selectedProductId) || null,
    [productStrip, selectedProductId],
  );

  const widgetSrc = `${BACKEND_URL || window.location.origin}/api/client-widgets/widget.js`;

  const loadAll = useCallback(async (pidFilter, keepSelection) => {
    setLoading(true);
    try {
      const params = {};
      if (pidFilter) params.product_id = pidFilter;
      const sitesRes = await axios.get(`${CW}/sites`, { ...authHeaders(), params });
      const list = (sitesRes.data?.sites || [])
        .filter((s) => !lockedProductId || s.product_id === lockedProductId);
      setSites(list);
      if (pidFilter) {
        setDefaultsDraft(sitesRes.data?.product_defaults || null);
      } else {
        setDefaultsDraft(null);
      }
      setSelectedSiteId((prev) => {
        if (keepSelection && prev && list.some((s) => s.site_id === prev)) return prev;
        if (prev && list.some((s) => s.site_id === prev)) return prev;
        return list[0]?.site_id || null;
      });
    } catch (err) {
      notifyError(errText(err, 'Failed to load client widgets'));
      setSites([]);
    } finally {
      setLoading(false);
    }
  }, [lockedProductId]);

  const loadClients = useCallback(async (pid) => {
    if (!pid) {
      setClients([]);
      return;
    }
    try {
      const r = await axios.get(`${CW}/clients/${encodeURIComponent(pid)}`, authHeaders());
      setClients(r.data?.clients || []);
    } catch {
      setClients([]);
    }
  }, []);

  const loadBranches = useCallback(async (pid, clientName) => {
    if (!pid || !clientName) {
      setBranches([]);
      setHasBranchCatalog(false);
      return;
    }
    try {
      const r = await axios.get(
        `${CW}/clients/${encodeURIComponent(pid)}/branches`,
        { ...authHeaders(), params: { client_name: clientName } },
      );
      setBranches(r.data?.branches || []);
      setHasBranchCatalog(!!r.data?.has_catalog);
    } catch {
      setBranches([]);
      setHasBranchCatalog(false);
    }
  }, []);

  useEffect(() => {
    if (!createProductId && products[0]?.product_id) {
      setCreateProductId(products[0].product_id);
    }
  }, [products, createProductId]);

  useEffect(() => {
    loadAll('');
  }, [loadAll]);

  useEffect(() => {
    if (!selectedProductId && products[0]?.product_id) {
      setSelectedProductId(products[0].product_id);
    }
  }, [products, selectedProductId]);

  useEffect(() => {
    if (selectedProductId) setCreateProductId(selectedProductId);
  }, [selectedProductId]);

  useEffect(() => {
    if (!selectedProductId) return;
    setSelectedSiteId((prev) => {
      if (prev && productSites.some((s) => s.site_id === prev)) return prev;
      return productSites[0]?.site_id || null;
    });
  }, [selectedProductId, productSites]);

  useEffect(() => {
    loadClients(createProductId);
    setNewClient('');
    setNewBranch('');
    setBranches([]);
    if (createProductId) {
      axios.get(`${CW}/product-defaults/${encodeURIComponent(createProductId)}`, authHeaders())
        .then((r) => setDefaultsDraft(r.data))
        .catch(() => {});
    }
  }, [createProductId, loadClients]);

  useEffect(() => {
    setNewBranch('');
    loadBranches(createProductId, newClient);
  }, [createProductId, newClient, loadBranches]);

  useEffect(() => {
    setKeyVisible(false);
    if (!selected) {
      setDraft(null);
      return;
    }
    const color = selected.primary_color
      || selected.effective?.primary_color
      || '#0F766E';
    const bg = selected.launcher_bg_color
      || selected.effective?.launcher_bg_color
      || color;
    const formFields = normalizeFormFields(
      selected.form_fields || selected.effective?.form_fields,
    );
    setDraft({
      enabled: selected.enabled !== false,
      accept_tickets: selected.accept_tickets !== false,
      view_tickets: selected.view_tickets !== false,
      chat_enabled: selected.chat_enabled !== false,
      attachments_enabled: formFields.attachments.enabled,
      max_attachments: selected.max_attachments ?? 5,
      max_attachment_mb: selected.max_attachment_mb ?? 10,
      allowed_domains: (selected.allowed_domains || []).join('\n'),
      application_url: selected.application_url || '',
      client_selection_mode: selected.client_selection_mode || 'fixed',
      form_fields: formFields,
      primary_color: color,
      launcher_mode: selected.launcher_mode || selected.effective?.launcher_mode || 'text',
      launcher_label: selected.launcher_label || selected.effective?.launcher_label || 'Support',
      launcher_icon: selected.launcher_icon || selected.effective?.launcher_icon || 'chat',
      launcher_icon_color: selected.launcher_icon_color || selected.effective?.launcher_icon_color || '#FFFFFF',
      launcher_bg_color: bg,
      position: selected.position || 'bottom-right',
      welcome_title: selected.welcome_title || '',
      welcome_subtitle: selected.welcome_subtitle || '',
      success_message: selected.success_message || '',
    });
  }, [selected]);

  const createSite = async () => {
    if (!createProductId) {
      notifyError('Pick a product');
      return;
    }
    if (needsCompanyAtCreate(newSelectionMode) && !newClient) {
      notifyError('Pick a company, or change selection mode so users pick it in the widget');
      return;
    }
    const appUrl = (newAppUrl || '').trim();
    if (appUrl && !hostFromAppUrl(appUrl)) {
      notifyError('Application URL must include a valid domain (e.g. https://app.client.com)');
      return;
    }
    setCreating(true);
    try {
      const r = await axios.post(`${CW}/sites`, {
        product_id: createProductId,
        client_selection_mode: newSelectionMode,
        client_name: needsCompanyAtCreate(newSelectionMode) ? newClient : '',
        branch: newSelectionMode === 'fixed' ? (newBranch || null) : null,
        application_url: appUrl,
        allowed_domains: appUrl && hostFromAppUrl(appUrl) ? [hostFromAppUrl(appUrl)] : [],
        environment: 'live',
        primary_color: defaultsDraft?.primary_color || '#0F766E',
        launcher_mode: defaultsDraft?.launcher_mode || 'text',
        launcher_label: defaultsDraft?.launcher_label || 'Support',
        launcher_icon: defaultsDraft?.launcher_icon || 'chat',
        launcher_icon_color: defaultsDraft?.launcher_icon_color || '#FFFFFF',
        launcher_bg_color: defaultsDraft?.launcher_bg_color || defaultsDraft?.primary_color || '#0F766E',
      }, authHeaders());
      notifySuccess(`Widget created — ${siteTitle(r.data)}`);
      setNewClient('');
      setNewBranch('');
      setNewSelectionMode('fixed');
      setNewAppUrl('');
      if (r.data?.product_id) setSelectedProductId(r.data.product_id);
      await loadAll('', true);
      setSelectedSiteId(r.data.site_id);
      setKeyVisible(true);
    } catch (err) {
      notifyError(errText(err, 'Failed to create widget site'));
    } finally {
      setCreating(false);
    }
  };

  const saveSite = async () => {
    if (!selected || !draft) return;
    const appUrl = String(draft.application_url || '').trim();
    if (appUrl && !hostFromAppUrl(appUrl)) {
      notifyError('Application URL must include a valid domain (e.g. https://app.client.com)');
      return;
    }
    setSaving(true);
    try {
      const color = (draft.primary_color || '#0F766E').trim();
      const formFields = normalizeFormFields(draft.form_fields);
      const appUrl = String(draft.application_url || '').trim();
      if (appUrl && !hostFromAppUrl(appUrl)) {
        notifyError('Application URL must include a valid domain (e.g. https://app.client.com)');
        return;
      }
      const updated = await axios.patch(`${CW}/sites/${selected.site_id}`, {
        enabled: !!draft.enabled,
        accept_tickets: !!draft.accept_tickets,
        view_tickets: !!draft.view_tickets,
        chat_enabled: draft.chat_enabled !== false,
        attachments_enabled: !!formFields.attachments.enabled,
        max_attachments: Number(draft.max_attachments) || 5,
        max_attachment_mb: Number(draft.max_attachment_mb) || 10,
        allowed_domains: mergeDomainLine(
          String(draft.allowed_domains || ''),
          hostFromAppUrl(draft.application_url),
        )
          .split(/[\n,]+/)
          .map((s) => s.trim())
          .filter(Boolean),
        application_url: String(draft.application_url || '').trim(),
        client_selection_mode: draft.client_selection_mode || 'fixed',
        form_fields: formFields,
        primary_color: color,
        launcher_mode: draft.launcher_mode === 'icon' ? 'icon' : 'text',
        launcher_label: draft.launcher_label,
        launcher_icon: draft.launcher_icon,
        launcher_icon_color: draft.launcher_icon_color,
        launcher_bg_color: draft.launcher_bg_color || color,
        position: draft.position,
        welcome_title: draft.welcome_title,
        welcome_subtitle: draft.welcome_subtitle,
        success_message: draft.success_message,
      }, authHeaders());
      notifySuccess('Saved — bubble color/settings update within ~1 minute (or refresh their page)');
      await loadAll('', true);
      // Keep draft in sync with what was saved
      if (updated.data?.primary_color) {
        setDraft((d) => (d ? { ...d, primary_color: updated.data.primary_color } : d));
      }
    } catch (err) {
      notifyError(errText(err, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const rotateKey = async () => {
    if (!selected) return;
    if (!window.confirm('Rotate site key? The old key stops working. Update the script on the client app.')) return;
    try {
      const r = await axios.post(`${CW}/sites/${selected.site_id}/rotate-key`, {}, authHeaders());
      notifySuccess('New site key generated');
      await loadAll('', true);
      setSelectedSiteId(r.data.site_id);
      setKeyVisible(true);
    } catch (err) {
      notifyError(errText(err, 'Failed to rotate key'));
    }
  };

  const generateIdentitySecret = async () => {
    if (!selected) return;
    const replacing = selected.has_identity_secret;
    const msg = replacing
      ? 'Replace the signing secret? Signatures made with the old secret stop working until the client application uses the new one.'
      : 'Generate a signing secret for this widget? You will see it once — give it to the client application team.';
    if (!window.confirm(msg)) return;
    try {
      const r = await axios.post(`${CW}/sites/${selected.site_id}/identity-secret`, {}, authHeaders());
      setIdentitySecret({ siteId: selected.site_id, value: r.data.identity_secret });
      notifySuccess('Signing secret generated — copy it now, it will not be shown again');
      await loadAll('', true);
      setSelectedSiteId(selected.site_id);
    } catch (err) {
      notifyError(errText(err, 'Failed to generate signing secret'));
    }
  };

  const setIdentityMode = async (mode) => {
    if (!selected) return;
    if (mode === 'required' && !window.confirm(
      'Require identity verification? From now on the widget only works when the client application '
      + 'sends a valid signature. Turn this on only after the client application has been updated.',
    )) return;
    try {
      await axios.patch(`${CW}/sites/${selected.site_id}`, { identity_verification: mode }, authHeaders());
      notifySuccess(mode === 'required' ? 'Identity verification is now required' : 'Identity verification turned off');
      await loadAll('', true);
      setSelectedSiteId(selected.site_id);
    } catch (err) {
      notifyError(errText(err, 'Failed to update identity verification'));
    }
  };

  const deleteSite = async () => {
    if (!selected) return;
    const where = siteTitle(selected);
    if (!window.confirm(
      `Delete widget for ${where}?\n\n`
      + 'The bubble will stop working on their site. Tickets already raised stay in ITSM.',
    )) return;
    try {
      await axios.delete(`${CW}/sites/${selected.site_id}`, authHeaders());
      notifySuccess('Widget deleted');
      setSelectedSiteId(null);
      await loadAll('', false);
    } catch (err) {
      notifyError(errText(err, 'Failed to delete'));
    }
  };

  const saveDefaults = async () => {
    if (!createProductId || !defaultsDraft) return;
    setSavingDefaults(true);
    try {
      await axios.put(`${CW}/product-defaults/${encodeURIComponent(createProductId)}`, {
        enabled: !!defaultsDraft.enabled,
        accept_tickets: !!defaultsDraft.accept_tickets,
        view_tickets: !!defaultsDraft.view_tickets,
        attachments_enabled: !!defaultsDraft.attachments_enabled,
        max_attachments: Number(defaultsDraft.max_attachments) || 5,
        max_attachment_mb: Number(defaultsDraft.max_attachment_mb) || 10,
        primary_color: defaultsDraft.primary_color,
        launcher_mode: defaultsDraft.launcher_mode === 'icon' ? 'icon' : 'text',
        launcher_label: defaultsDraft.launcher_label,
        launcher_icon: defaultsDraft.launcher_icon,
        launcher_icon_color: defaultsDraft.launcher_icon_color,
        launcher_bg_color: defaultsDraft.launcher_bg_color || defaultsDraft.primary_color,
        position: defaultsDraft.position,
        welcome_title: defaultsDraft.welcome_title,
        welcome_subtitle: defaultsDraft.welcome_subtitle,
        success_message: defaultsDraft.success_message,
      }, authHeaders());
      notifySuccess('Product defaults saved (apply to newly created widgets)');
    } catch (err) {
      notifyError(errText(err, 'Failed to save defaults'));
    } finally {
      setSavingDefaults(false);
    }
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      notifySuccess(`${label} copied`);
    } catch {
      window.prompt('Copy:', text);
    }
  };

  const snippetFor = (site) => (
    `<script src="${widgetSrc}" data-site-key="${site.site_key}"
        data-name="{{user.name}}"
        data-email="{{user.email}}"${site.has_identity_secret ? `
        data-signature="{{hmac_sha256(secret, lowercase(user.email))}}"` : ''}
        data-phone="{{user.phone}}"
        data-branch="{{user.branch}}"
        data-company="{{user.company}}"
        async></script>`
  );

  const toggle = (key) => setDraft((d) => (d ? { ...d, [key]: !d[key] } : d));

  const maskKey = (key) => {
    if (!key || key.length < 12) return key || '';
    return `${key.slice(0, 10)}…${key.slice(-6)}`;
  };

  return (
    <div style={{ padding: '8px 0 24px' }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 16, fontWeight: 600 }}>Client widgets</div>
        <div style={{ fontSize: 13, color: '#6B7280', marginTop: 4, maxWidth: 760 }}>
          Bind a widget to a <strong>product</strong>. Lock a company here, or let the visitor pick
          company and/or branch in the bubble. After changing settings, click{' '}
          <strong>Save settings</strong> — the client bubble refreshes within ~45s.
        </div>
      </div>

      {!lockedProductId && (
      <div className="pm-card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, margin: 0, color: '#111827' }}>
            Products ({productStrip.length})
            {loading ? ' · loading…' : ''}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            {productStrip.length > 0 && (
              <ListSearch
                value={productSearch}
                onChange={setProductSearch}
                placeholder="Search products…"
                resultCount={filteredProductStrip.length}
                totalCount={productStrip.length}
                maxWidth="280px"
                style={{ marginBottom: 0, minWidth: 220 }}
              />
            )}
            <div style={{ fontSize: 12, color: '#6B7280' }}>
              {sites.length} widget{sites.length === 1 ? '' : 's'} total
            </div>
          </div>
        </div>
        {productStrip.length === 0 ? (
          <p style={{ color: '#9CA3AF', fontSize: 12, margin: 0 }}>No products yet. Create a product first, then add a widget.</p>
        ) : (
          filteredProductStrip.length === 0 ? (
            <p style={{ color: '#9CA3AF', fontSize: 12, margin: 0 }}>No products match "{productSearch}".</p>
          ) : (
          <div className="pm-product-strip">
            {filteredProductStrip.map((p) => {
              const active = selectedProductId === p.productId;
              return (
                <button
                  key={p.productId}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setSelectedProductId(p.productId)}
                  style={{
                    flex: '0 0 auto',
                    minWidth: 168,
                    maxWidth: 220,
                    minHeight: 72,
                    textAlign: 'left',
                    padding: '12px 14px',
                    border: active ? '2px solid #002FA7' : '1px solid #E5E7EB',
                    borderRadius: 12,
                    background: active ? '#EFF6FF' : '#FFFFFF',
                    cursor: 'pointer',
                    boxShadow: active ? '0 2px 8px rgba(0, 47, 167, 0.10)' : 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Package size={18} color={active ? '#002FA7' : '#6B7280'} weight={active ? 'fill' : 'regular'} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{
                        fontWeight: 600,
                        fontSize: 12,
                        color: '#111827',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      >
                        {p.productName}
                      </div>
                      <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>
                        {p.widgetCount} widget{p.widgetCount === 1 ? '' : 's'}
                        {p.productKey ? ` · ${p.productKey}` : ''}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          )
        )}
      </div>
      )}

      {/* Create row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 12,
        alignItems: 'end',
        marginBottom: 16,
        padding: 14,
        border: '1px solid #E5E7EB',
        borderRadius: 10,
        background: '#F9FAFB',
      }}
      >
        <div style={{ minWidth: 0 }}>
          <span style={labelStyle}>Product</span>
          <SearchableSelect
            options={productOptions}
            value={createProductId}
            onChange={(v) => {
              const pid = v || '';
              setCreateProductId(pid);
              if (pid) setSelectedProductId(pid);
            }}
            placeholder="Select product"
            searchPlaceholder="Search products…"
          />
        </div>
        <div style={{ minWidth: 0 }}>
          <span style={labelStyle}>Company / branch selection</span>
          <SearchableSelect
            options={selectionModeOptions}
            value={newSelectionMode}
            onChange={(v) => setNewSelectionMode(v || 'fixed')}
            placeholder="Selection mode"
            searchPlaceholder="Search…"
          />
        </div>
        {needsCompanyAtCreate(newSelectionMode) && (
          <>
            <div style={{ minWidth: 0 }}>
              <span style={labelStyle}>Client / company</span>
              <SearchableSelect
                options={clientOptions}
                value={newClient}
                onChange={(v) => setNewClient(v || '')}
                placeholder="Select client"
                searchPlaceholder="Search clients…"
              />
            </div>
            {newSelectionMode === 'fixed' && (
              <div style={{ minWidth: 0 }}>
                <span style={labelStyle}>Branch (optional)</span>
                <SearchableSelect
                  options={branchOptions}
                  value={newBranch}
                  onChange={(v) => setNewBranch(v || '')}
                  placeholder={!newClient
                    ? 'Select a client first'
                    : (hasBranchCatalog && branches.length === 0
                      ? 'No branches for this company'
                      : 'Company-level or pick branch')}
                  searchPlaceholder="Search branches…"
                  disabled={!newClient}
                />
              </div>
            )}
          </>
        )}
        <div style={{ minWidth: 0, gridColumn: '1 / -1' }}>
          <span style={labelStyle}>Application URL</span>
          <input
            style={inputStyle}
            type="url"
            inputMode="url"
            autoComplete="url"
            placeholder="https://app.client.com"
            value={newAppUrl}
            onChange={(e) => setNewAppUrl(e.target.value)}
            aria-label="Application URL"
          />
          <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
            {hostFromAppUrl(newAppUrl)
              ? `Will add ${hostFromAppUrl(newAppUrl)} to this widget’s allowed domains and CORS — no server env change needed.`
              : 'Paste the client app URL. Its domain is added to allowed domains and CORS for this widget.'}
          </div>
        </div>
        <button
          type="button"
          className="tk-btn tk-btn-primary"
          disabled={creating || !createProductId || (needsCompanyAtCreate(newSelectionMode) && !newClient)}
          onClick={createSite}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', height: 38 }}
        >
          <Plus size={15} weight="bold" /> Create widget
        </button>
      </div>

      {defaultsDraft && createProductId && (
        <details style={{ marginBottom: 16, border: '1px solid #E5E7EB', borderRadius: 10, padding: '10px 14px', background: '#fff' }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
            Defaults for new widgets on this product (super admin)
          </summary>
          <p style={{ fontSize: 12, color: '#6B7280', margin: '8px 0' }}>
            These apply when you create a new widget. Edit an existing widget below to change its launcher.
          </p>
          <div style={{ marginBottom: 12 }}>
            <LauncherConfigFields
              value={defaultsDraft}
              onChange={setDefaultsDraft}
              labelStyle={labelStyle}
              inputStyle={inputStyle}
              title="Default launcher button"
            />
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <span style={labelStyle}>Panel / header color</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="color"
                  value={/^#[0-9A-Fa-f]{6}$/.test(defaultsDraft.primary_color) ? defaultsDraft.primary_color : '#0F766E'}
                  onChange={(e) => setDefaultsDraft((d) => ({ ...d, primary_color: e.target.value }))}
                />
                <input
                  style={{ ...inputStyle, width: 110 }}
                  value={defaultsDraft.primary_color || ''}
                  onChange={(e) => setDefaultsDraft((d) => ({ ...d, primary_color: e.target.value }))}
                />
              </div>
            </div>
            <button type="button" className="tk-btn" disabled={savingDefaults} onClick={saveDefaults}>
              {savingDefaults ? 'Saving…' : 'Save defaults'}
            </button>
          </div>
        </details>
      )}

      {selectedProduct && productSites.length > 0 && (
        <div className="pm-card" style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: '#111827' }}>
            Widgets on {selectedProduct.productName}
          </div>
          <div className="pm-product-strip">
            {productSites.map((s) => {
              const active = selectedSiteId === s.site_id;
              const live = s.enabled !== false;
              return (
                <button
                  key={s.site_id}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setSelectedSiteId(s.site_id)}
                  style={{
                    flex: '0 0 auto',
                    minWidth: 200,
                    maxWidth: 280,
                    minHeight: 72,
                    textAlign: 'left',
                    padding: '12px 14px',
                    border: active ? '2px solid #0F766E' : '1px solid #E5E7EB',
                    borderRadius: 12,
                    background: active ? '#ECFDF5' : '#FFFFFF',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <LauncherPreview
                      compact
                      mode={s.launcher_mode || 'text'}
                      label={s.launcher_label || 'Support'}
                      icon={s.launcher_icon || 'chat'}
                      iconColor={s.launcher_icon_color || '#FFFFFF'}
                      bgColor={s.launcher_bg_color || s.primary_color || '#0F766E'}
                    />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{
                          fontWeight: 600,
                          fontSize: 12,
                          color: '#0F172A',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                          minWidth: 0,
                        }}
                        >
                          {placementLabel(s)}
                        </div>
                        <span style={{
                          flexShrink: 0,
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: '0.02em',
                          textTransform: 'uppercase',
                          padding: '2px 6px',
                          borderRadius: 999,
                          background: live ? '#D1FAE5' : '#F3F4F6',
                          color: live ? '#065F46' : '#6B7280',
                        }}
                        >
                          {live ? 'Live' : 'Hidden'}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                        {s.stats?.tickets_total ?? 0} tickets
                        {s.application_url ? ` · ${hostFromAppUrl(s.application_url) || s.application_url}` : ''}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <section style={{ border: '1px solid #E5E7EB', borderRadius: 10, padding: 16, background: '#fff', minHeight: 240 }}>
          {!selectedProductId ? (
            <div style={{ color: '#9CA3AF', fontSize: 13 }}>Select a product above to create or edit its widget.</div>
          ) : !selected || !draft ? (
            <div style={{ color: '#9CA3AF', fontSize: 13 }}>
              No widget on {selectedProduct?.productName || 'this product'} yet. Use <strong>Create widget</strong> above.
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14, alignItems: 'flex-start' }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: '#0F766E',
                    background: '#F0FDFA',
                    border: '1px solid #99F6E4',
                    borderRadius: 999,
                    padding: '3px 10px',
                    marginBottom: 8,
                  }}
                  >
                    Product
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#0F172A', lineHeight: 1.3 }}>
                    {productLabel(selected)}
                  </div>
                  <div style={{ fontSize: 13, color: '#4B5563', marginTop: 4 }}>
                    {placementLabel(selected)}
                  </div>
                  <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                    {selected.enabled === false ? 'Hidden' : 'Live'}
                    {' · '}
                    {selected.site_id}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button type="button" className="tk-btn" onClick={() => copyText(snippetFor(selected), 'Install snippet')}>
                    <Copy size={14} /> Copy install snippet
                  </button>
                  <button type="button" className="tk-btn" onClick={rotateKey}>
                    <ArrowClockwise size={14} /> Rotate key
                  </button>
                  <button type="button" className="tk-btn" style={{ color: '#B91C1C' }} onClick={deleteSite}>
                    <Trash size={14} /> Delete
                  </button>
                </div>
              </div>

              {/* Site key — always visible */}
              <div style={{
                marginBottom: 14, padding: 12, borderRadius: 8, background: '#F3F4F6', border: '1px solid #E5E7EB',
              }}
              >
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Site key</div>
                <div style={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: 12, wordBreak: 'break-all', marginBottom: 8,
                }}
                >
                  {keyVisible ? selected.site_key : maskKey(selected.site_key)}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" className="tk-btn" onClick={() => setKeyVisible((v) => !v)}>
                    <Eye size={14} /> {keyVisible ? 'Hide key' : 'Show full key'}
                  </button>
                  <button type="button" className="tk-btn" onClick={() => copyText(selected.site_key, 'Site key')}>
                    <Copy size={14} /> Copy key
                  </button>
                  <a
                    className="tk-btn"
                    href={`${BACKEND_URL || ''}/api/client-widgets/demo?site_key=${encodeURIComponent(selected.site_key)}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    Open web demo
                  </a>
                  <a
                    className="tk-btn"
                    href={`${BACKEND_URL || ''}/api/client-widgets/mobile?site_key=${encodeURIComponent(selected.site_key)}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    Open mobile page
                  </a>
                </div>
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 8 }}>
                  Bind the signed-in user by replacing the placeholders (name, email, phone, branch).
                  Leave an attribute off if the visitor should type it.
                  Web install snippet:
                  <code style={{ display: 'block', marginTop: 4, wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
                    {snippetFor(selected)}
                  </code>
                  Mobile WebView URL (form opens immediately):
                  <code style={{ display: 'block', marginTop: 4, wordBreak: 'break-all' }}>
                    {`${BACKEND_URL || window.location.origin}/api/client-widgets/mobile?site_key=${selected.site_key}`}
                  </code>
                </div>
              </div>

              {/* Identity verification — signed user identity from the client application */}
              <div style={{
                marginBottom: 14, padding: 12, borderRadius: 8,
                background: selected.identity_verification === 'required' ? '#ECFDF5' : '#FFFBEB',
                border: `1px solid ${selected.identity_verification === 'required' ? '#A7F3D0' : '#FDE68A'}`,
              }}
              >
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                  Identity verification:{' '}
                  {selected.identity_verification === 'required'
                    ? <span style={{ color: '#047857' }}>Required</span>
                    : <span style={{ color: '#B45309' }}>Off</span>}
                </div>
                <div style={{ fontSize: 11, color: '#4B5563', marginBottom: 8, maxWidth: 760 }}>
                  When required, the widget only works for the user the client application vouches for:
                  its <strong>server</strong> sends <code>data-signature</code> = HMAC-SHA256(secret, lower-case email).
                  Nobody can see or create tickets as someone else by typing an email.
                  {selected.identity_verification !== 'required' && (
                    <> While off, anyone with the page can type any email.</>
                  )}
                </div>
                {identitySecret && identitySecret.siteId === selected.site_id && (
                  <div style={{
                    marginBottom: 8, padding: 8, borderRadius: 6, background: '#fff', border: '1px dashed #9CA3AF',
                  }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>
                      Signing secret — shown once. Store it on the client application server only.
                    </div>
                    <code style={{ display: 'block', fontSize: 12, wordBreak: 'break-all', marginBottom: 6 }}>
                      {identitySecret.value}
                    </code>
                    <button type="button" className="tk-btn" onClick={() => copyText(identitySecret.value, 'Signing secret')}>
                      <Copy size={14} /> Copy secret
                    </button>
                    <div style={{ fontSize: 11, color: '#6B7280', marginTop: 6 }}>
                      Server example (Python):
                      <code style={{ display: 'block', whiteSpace: 'pre-wrap', marginTop: 2 }}>
                        {"hmac.new(SECRET.encode(), user.email.strip().lower().encode(), hashlib.sha256).hexdigest()"}
                      </code>
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" className="tk-btn" onClick={generateIdentitySecret}>
                    {selected.has_identity_secret ? 'Replace signing secret' : 'Generate signing secret'}
                  </button>
                  {selected.identity_verification === 'required' ? (
                    <button type="button" className="tk-btn" onClick={() => setIdentityMode('off')}>
                      Turn off
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="tk-btn"
                      disabled={!selected.has_identity_secret}
                      title={selected.has_identity_secret ? '' : 'Generate a signing secret first'}
                      onClick={() => setIdentityMode('required')}
                    >
                      Require verification
                    </button>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: 14, maxWidth: 420 }}>
                <span style={labelStyle}>Company / branch selection</span>
                <SearchableSelect
                  options={selectionModeOptions}
                  value={draft.client_selection_mode || 'fixed'}
                  onChange={(v) => setDraft({ ...draft, client_selection_mode: v || 'fixed' })}
                  placeholder="Selection mode"
                  searchPlaceholder="Search…"
                />
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
                  {draft.client_selection_mode === 'pick_branch'
                    ? (selected.client_name
                      ? `Company stays ${selected.client_name}. Visitors pick a branch in the bubble.`
                      : 'This widget has no company. Create a new widget with a company selected, then choose this mode.')
                    : draft.client_selection_mode === 'fixed'
                      ? 'Company (and optional branch) stay locked on this site key.'
                      : 'Product stays bound to this site key. Switching to a company picker clears any fixed company on save.'}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10, marginBottom: 14 }}>
                {[
                  ['enabled', 'Widget enabled (show bubble)'],
                  ['accept_tickets', 'Accept new tickets'],
                  ['view_tickets', 'View tickets / status'],
                  ['chat_enabled', 'Client chat (beside My tickets)'],
                ].map(([key, label]) => (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '8px 10px', background: '#F9FAFB', borderRadius: 8 }}>
                    <input type="checkbox" checked={!!draft[key]} onChange={() => toggle(key)} />
                    {label}
                  </label>
                ))}
              </div>

              <div style={{
                marginBottom: 14, padding: 12, borderRadius: 10, border: '1px solid #E5E7EB', background: '#F9FAFB',
              }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Raise-ticket form fields</div>
                <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 10 }}>
                  Choose which fields appear in the widget and whether each is mandatory. Email is always required.
                </div>
                <div style={{ display: 'grid', gap: 8 }}>
                  {FORM_FIELD_ROWS.map(({ key, title, lock }) => {
                    const row = (draft.form_fields && draft.form_fields[key]) || DEFAULT_FORM_FIELDS[key];
                    return (
                      <div
                        key={key}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr auto auto',
                          gap: 12,
                          alignItems: 'center',
                          padding: '8px 10px',
                          background: '#fff',
                          borderRadius: 8,
                          border: '1px solid #E5E7EB',
                          fontSize: 13,
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 600 }}>{title}</div>
                          {lock ? (
                            <div style={{ fontSize: 11, color: '#6B7280' }}>Always shown & required</div>
                          ) : null}
                        </div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                          <input
                            type="checkbox"
                            checked={!!row.enabled}
                            disabled={lock}
                            onChange={(e) => {
                              const enabled = e.target.checked;
                              setDraft((d) => ({
                                ...d,
                                form_fields: {
                                  ...normalizeFormFields(d.form_fields),
                                  [key]: {
                                    ...normalizeFormFields(d.form_fields)[key],
                                    enabled,
                                    required: enabled ? normalizeFormFields(d.form_fields)[key].required : false,
                                  },
                                },
                                ...(key === 'attachments' ? { attachments_enabled: enabled } : {}),
                              }));
                            }}
                          />
                          Show
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                          <input
                            type="checkbox"
                            checked={!!row.required}
                            disabled={lock || !row.enabled}
                            onChange={(e) => {
                              const required = e.target.checked;
                              setDraft((d) => ({
                                ...d,
                                form_fields: {
                                  ...normalizeFormFields(d.form_fields),
                                  [key]: {
                                    ...normalizeFormFields(d.form_fields)[key],
                                    required,
                                  },
                                },
                              }));
                            }}
                          />
                          Required
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12,
                padding: 12, background: '#F8FAFC', borderRadius: 10, border: '1px solid #E2E8F0', marginBottom: 12,
              }}
              >
                <div style={{ gridColumn: '1 / -1', fontSize: 12, fontWeight: 600, color: '#334155' }}>
                  Panel branding (header / accents)
                </div>
                <div>
                  <span style={labelStyle}>Primary / panel color</span>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="color"
                      value={/^#[0-9A-Fa-f]{6}$/.test(draft.primary_color) ? draft.primary_color : '#0F766E'}
                      onChange={(e) => setDraft({ ...draft, primary_color: e.target.value })}
                    />
                    <input
                      style={{ ...inputStyle, width: 110 }}
                      value={draft.primary_color}
                      onChange={(e) => setDraft({ ...draft, primary_color: e.target.value })}
                      placeholder="#0F766E"
                    />
                  </div>
                </div>
                <div>
                  <span style={labelStyle}>Position</span>
                  <select style={inputStyle} value={draft.position} onChange={(e) => setDraft({ ...draft, position: e.target.value })}>
                    <option value="bottom-right">Bottom right</option>
                    <option value="bottom-left">Bottom left</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <LauncherConfigFields
                  value={draft}
                  onChange={setDraft}
                  labelStyle={labelStyle}
                  inputStyle={inputStyle}
                  title="Launcher button"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                <div>
                  <span style={labelStyle}>Max attachments</span>
                  <input style={inputStyle} type="number" min={0} max={20} value={draft.max_attachments} onChange={(e) => setDraft({ ...draft, max_attachments: e.target.value })} />
                </div>
                <div>
                  <span style={labelStyle}>Max MB / file</span>
                  <input style={inputStyle} type="number" min={1} max={50} value={draft.max_attachment_mb} onChange={(e) => setDraft({ ...draft, max_attachment_mb: e.target.value })} />
                </div>
              </div>

              <div style={{ marginTop: 12 }}>
                <span style={labelStyle}>Application URL</span>
                <input
                  style={inputStyle}
                  type="url"
                  inputMode="url"
                  autoComplete="url"
                  placeholder="https://app.client.com"
                  value={draft.application_url || ''}
                  onChange={(e) => setDraft({ ...draft, application_url: e.target.value })}
                  onBlur={(e) => {
                    const host = hostFromAppUrl(e.target.value);
                    if (!host) return;
                    setDraft((d) => (d ? {
                      ...d,
                      allowed_domains: mergeDomainLine(d.allowed_domains, host),
                    } : d));
                  }}
                  aria-label="Application URL"
                />
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
                  {hostFromAppUrl(draft.application_url)
                    ? `Domain ${hostFromAppUrl(draft.application_url)} is added to allowed domains and CORS on save — no server restart.`
                    : 'Paste the client app URL to add its domain to the allowlist and CORS for this widget.'}
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <span style={labelStyle}>Allowed domains (one per line; leave empty to allow any)</span>
                <textarea
                  style={{ ...inputStyle, minHeight: 64, fontFamily: 'ui-monospace, monospace' }}
                  value={draft.allowed_domains}
                  onChange={(e) => setDraft({ ...draft, allowed_domains: e.target.value })}
                  placeholder="jayapriya.easychit.in"
                />
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
                  This widget only. Use the client host (`jayapriya.easychit.in`), not `easychit.in`.
                  Open web demo runs on ITSM, not the client site — that is allowed separately after deploy.
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <span style={labelStyle}>Welcome title</span>
                <input style={inputStyle} value={draft.welcome_title} onChange={(e) => setDraft({ ...draft, welcome_title: e.target.value })} />
              </div>
              <div style={{ marginTop: 12 }}>
                <span style={labelStyle}>Welcome subtitle</span>
                <input style={inputStyle} value={draft.welcome_subtitle} onChange={(e) => setDraft({ ...draft, welcome_subtitle: e.target.value })} />
              </div>
              <div style={{ marginTop: 12 }}>
                <span style={labelStyle}>Success message (use {'{ticket_no}'})</span>
                <input style={inputStyle} value={draft.success_message} onChange={(e) => setDraft({ ...draft, success_message: e.target.value })} />
              </div>

              <button
                type="button"
                className="tk-btn tk-btn-primary"
                style={{ marginTop: 16, padding: '10px 18px' }}
                disabled={saving}
                onClick={saveSite}
              >
                {saving ? 'Saving…' : 'Save settings'}
              </button>
            </>
          )}
        </section>
    </div>
  );
}
